/**
 * emailLinkArrival — one PostHog event when the app loads from a tagged email
 * link (2026-11-03, daily practice summary rollout to Umuzi).
 *
 * WHY an explicit event: Resend click tracking stays OFF (it rewrites every
 * link, including magic links; Gilbert, 2026-08-05, email.md §6). Our emails
 * instead tag in-app links with `?src=<campaign>&lk=<link kind>&d=<date>`.
 * Measured 2026-11-03 with a probe link: the `$pageview` fires AFTER the
 * /scenarios/:id/login redirect, so its `$current_url` has lost the tag; only
 * `$session_entry_url` keeps it, and only when the click starts a new PostHog
 * session (a learner active in the last 30 minutes continues the old session,
 * whose entry URL is the earlier page). Capturing here, before the router
 * mounts, records every tagged arrival.
 *
 * LOOP CLOSURE (CLAUDE.md):
 *   writer:           captureEmailLinkArrival(), called once from main.jsx.
 *   scheduled reader: docs/DATED-CHECKS.md rows for the daily-summary rollout
 *                     (census first: SELECT properties.src, properties.lk,
 *                     count() GROUP BY 1,2), plus
 *                     scripts/daily-summary-engagement-read.mjs for the DB half.
 *   what changes:     the keep / extend / stop call on the per-org daily
 *                     summary; one distinct `lk` value across a week of sends
 *                     is a dead instrument, not a result.
 *
 * PRIVACY: `src`, `lk` and `d` are short enums/dates set by our own email
 * builders; anything that does not match the shapes below is dropped, so a
 * hand-edited URL cannot smuggle free text into analytics.
 * FAIL-OPEN: an absent or throwing posthog never breaks page load.
 */

export const EMAIL_LINK_EVENT = 'email_link_clicked';

const SRC_RE = /^[a-z0-9-]{1,40}$/;
const LK_RE = /^[a-z_]{1,30}$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Pure: the event properties for a URL, or null when it is not a tagged email link.
 * @param {string} href
 */
export function emailLinkProps(href) {
  let url;
  try { url = new URL(href); } catch { return null; }
  const src = url.searchParams.get('src');
  if (!src || !SRC_RE.test(src)) return null;
  const lk = url.searchParams.get('lk');
  const d = url.searchParams.get('d');
  return {
    src,
    lk: lk && LK_RE.test(lk) ? lk : 'not_set',
    sent_for_date: d && DATE_RE.test(d) ? d : 'not_set',
    landing_path: url.pathname.replace(/\/\d+(?=\/|$)/g, '/:id'),
  };
}

/** Capture once for the current page load. Never throws. */
export function captureEmailLinkArrival(posthog, href = typeof window !== 'undefined' ? window.location.href : '') {
  try {
    const props = emailLinkProps(href);
    if (!props || !posthog || typeof posthog.capture !== 'function') return false;
    posthog.capture(EMAIL_LINK_EVENT, props);
    return true;
  } catch {
    return false;
  }
}
